import { Card, CardBody, CardHeader, CardTitle, Alert } from '@/common'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { AddAssetWithIdPayload, EmissionPointsEditorProps } from './useAssetInstances'
import { StepFooter } from '@/shared/components'
import { CastingSystem } from './AluminiumPrimarySmeltingEmissionPointsEditor'
import { EmissionsControlAssetEditor } from './components/EmissionsControlAssetEditor'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import AssetCategoryEditor from './components/AssetCategoryEditor'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

function PreTreatment({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  id
}: {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (assetIds: number[]) => void
  id: string
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config

  const pyrometallurgicalCleaningAssetDefinitions = [
    assetsById['rotary_dryers__rotary_kiln__rotary_drum'],
    assetsById['fluid_bed_dryer__fluid_bed_decoater']
  ]

  const preTreatmentRecommendedFurnaceAssetDefinitions = [
    assetsById['hearth_furnace.top_loaded'],
    assetsById['hearth_furnace.round'],
    assetsById['hearth_furnace.closed_well'],
    assetsById['reverberatory.barrel_shaped_furnace'],
    assetsById['reverberatory.standard'],
    assetsById['reverberatory.oval_shaped'],
    assetsById['crucible_induction_furnace'],
    assetsById['crucible_channel_induction_furnace']
  ]

  const preTreatmentOtherFurnaceAssetDefinitions = [
    assetsById['hearth_furnace.side_well'],
    assetsById['hearth_furnace.dry_hearth'],
    assetsById['hearth_furnace.dry_hearth_with_bottom_heating'],
    assetsById['hearth_furnace.tower_furnace__shaft_furnace__stack_furnace'],
    assetsById['crucible_furnace'],
    assetsById['other_holding_furnace'],
    assetsById['rotary.fixed_axis'],
    assetsById['rotary.tilting'],
    assetsById['sweat_furnace'],
    assetsById['car_bottom_furnace'],
    assetsById['multichamber_furnace']
  ]

  const furnaceAssetDefinitions = [
    ...preTreatmentRecommendedFurnaceAssetDefinitions,
    ...preTreatmentOtherFurnaceAssetDefinitions
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle title={t(`emissionPoints:pre_treatment.title`)} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info" className="whitespace-pre-line">
            {t(`emissionPoints:pre_treatment.callout`)}
          </Alert>
          <AssetCategoryEditor
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            category="pyrometallurgical_cleaning"
            assetDefinitions={pyrometallurgicalCleaningAssetDefinitions}
            title={t(
              'emissionPoints:pre_treatment.assetCategories.pyrometallurgical_cleaning.title'
            )}
            technologyTypeQuestion={t(
              'emissionPoints:pre_treatment.assetCategories.pyrometallurgical_cleaning.technologyTypeQuestion'
            )}
          />

          <AssetCategoryEditor
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            category="pre_heating_or_drying_furnaces"
            assetDefinitions={furnaceAssetDefinitions}
            title={t(
              'emissionPoints:pre_treatment.assetCategories.pre_heating_or_drying_furnaces.title'
            )}
            technologyTypeQuestion={t(
              'emissionPoints:pre_treatment.assetCategories.pre_heating_or_drying_furnaces.technologyTypeQuestion'
            )}
          />
        </CardBody>
      </Card>
    </>
  )
}

function RefiningProcess({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  id
}: {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (assetIds: number[]) => void
  id: string
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config

  const refiningRecommendedFurnaceAssetDefinitions = [
    assetsById['reverberatory.standard'],
    assetsById['hearth_furnace.top_loaded'],
    assetsById['multichamber_furnace'],
    assetsById['hearth_furnace.dry_hearth'],
    assetsById['hearth_furnace.dry_hearth_with_bottom_heating'],
    assetsById['reverberatory.barrel_shaped_furnace'],
    assetsById['reverberatory.oval_shaped'],
    assetsById['rotary.fixed_axis'],
    assetsById['rotary.tilting'],
    assetsById['crucible_induction_furnace']
  ]

  const refiningOtherFurnaceAssetDefinitions = [
    assetsById['crucible_furnace'],
    assetsById['crucible_channel_induction_furnace'],
    assetsById['hearth_furnace.round'],
    assetsById['hearth_furnace.side_well'],
    assetsById['hearth_furnace.closed_well'],
    assetsById['hearth_furnace.tower_furnace__shaft_furnace__stack_furnace'],
    assetsById['other_holding_furnace'],
    assetsById['car_bottom_furnace'],
    assetsById['sweat_furnace']
  ]

  const furnaceAssetDefinitions = [
    ...refiningRecommendedFurnaceAssetDefinitions,
    ...refiningOtherFurnaceAssetDefinitions
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle title={t(`emissionPoints:refining_process.title`)} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info" className="whitespace-pre-line">
            {t(`emissionPoints:refining_process.callout`)}
          </Alert>
          <AssetCategoryEditor
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            assetDefinitions={furnaceAssetDefinitions}
            title={t(`emissionPoints:refining_process.assetCategories.refining_furnaces.title`)}
            technologyTypeQuestion={t(
              `emissionPoints:refining_process.assetCategories.refining_furnaces.technologyTypeQuestion`
            )}
          />
        </CardBody>
      </Card>
    </>
  )
}

export default function AluminiumSecondaryMeltingEmissionPointsEditor({
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  selectedAssets,
  onPreviousClick,
  onNextClick
}: EmissionPointsEditorProps) {
  const preTreatmentAssets = selectedAssets.filter(asset => asset.emissionPoint === 'pre_treatment')
  const refiningProcessAssets = selectedAssets.filter(
    asset => asset.emissionPoint === 'refining_process'
  )
  const castingSystemAssets = selectedAssets.filter(
    asset => asset.emissionPoint === 'casting_system'
  )
  const emissionsControlAssets = selectedAssets.filter(
    asset => asset.emissionPoint === 'emissions_control'
  )
  const [areEmissionsControlAssetsUsed, setAreEmissionsControlAssetsUsed] = useState(false)

  const isNextEnabled =
    preTreatmentAssets.length > 0 &&
    refiningProcessAssets.length > 0 &&
    castingSystemAssets.length > 0 &&
    ((areEmissionsControlAssetsUsed && emissionsControlAssets.length > 0) ||
      !areEmissionsControlAssetsUsed)

  return (
    <div className="space-y-4">
      <PreTreatment
        selectedAssets={preTreatmentAssets}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="pre_treatment"
      />
      <RefiningProcess
        selectedAssets={refiningProcessAssets}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="refining_process"
      />
      <CastingSystem
        selectedAssets={castingSystemAssets}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="casting_system"
      />
      <EmissionsControlAssetEditor
        selectedAssets={emissionsControlAssets}
        areAssetsUsed={areEmissionsControlAssetsUsed}
        updateAreAssetsUsed={setAreEmissionsControlAssetsUsed}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="emissions_control"
      />
      <StepFooter onPrevious={onPreviousClick} onNext={onNextClick} isNextEnabled={isNextEnabled} />
    </div>
  )
}
